import { Fragment, h } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { UPDATE_SETTING } from '../../../../../store/types';
import { SETTINGS_CLOSED, SETTINGS_OPENED, USER_AUDIO_TRACK_CHANGED, USER_AUDIO_TRACK_LIST_OPENED, USER_CLICK, USER_PLAYBACKRATE_CHANGE, USER_QUALITY_MENU_OPENED, USER_REPORT_PROBLEM, USER_REPORT_QUALITY, USER_SETTINGS_UPDATED, USER_SUBTITLE_SETTINGS_OPENED, USER_SUBTITLE_TRACK_LIST_OPENED, USER_TEXT_TRACK_CHANGED } from '../../../../../types';
import { deepMerge } from '../../../../../utils';
import { BUTTON_LAYER } from '../../../../common/constants';
import { useOpenDialogsCounter, useRegisterDialog } from '../../../../common/context/DialogContext';
import { usePlayerContext, useStyles } from '../../../../common/hooks';
import { TEXT_LEVEL_2_FONT_SIZE } from '../../../../common/styles';
import { connect, withBreakPoints } from '../../../hoc';
import { BUTTON_SQUARE_SIZE, ROWITEM_SUBLABEL_ICON } from '../../../styles';
import { handleCloseModalButton } from '../../../utils/modal';
import { Button, ToggleButton } from '../../common';
import RowItem from '../../common/menu/RowItem';
import Selectable from '../../common/menu/Selectable';
import Seperator from '../../common/menu/Seperator';
import SlidableSlide from '../../common/menu/SlidableSlide';
import SlideContent from '../../common/menu/SlideContent';
import SlideMenu from '../../common/menu/SlideMenu';
import { Modal } from '../../common/modal';
import Quality from './Quality';
import Reporter from './Reporter';
import { FEEDBACK_THANKS_TEXT_STYLE, NAVBAR_STYLE, SETTINGS_ICON_SIZE } from './styles';
import TextTrackSettings from './TextTrackSettings';

const PLAYBACK_SPEED_MAP = [
  { label: 'x 0.25', ariaLabel: 'fois 0.25', value: 0.25 },
  { label: 'x 0.5', ariaLabel: 'fois 0.5', value: 0.5 },
  { label: 'x 0.75', ariaLabel: 'fois 0.75', value: 0.75 },
  { label: 'Normale', ariaLabel: 'Normale', value: 1 },
  { label: 'x 1.25', ariaLabel: 'fois 1.25', value: 1.25 },
  { label: 'x 1.5', ariaLabel: 'fois 1.25', value: 1.5 },
  { label: 'x 1.75', ariaLabel: 'fois 1.25', value: 1.75 }
];

const REPORT_PROBLEM_MAP = [
  { label: 'L\'écran de la vidéo est noir', value: 1 },
  { label: 'La vidéo ne se lance pas', value: 2 },
  { label: 'Le son et l\'image sont désynchronisés', value: 3 },
  { label: 'La vidéo est saccadée / coupure', value: 4 },
  { label: 'Les sous-titres sont mal calés', value: 5 },
  { label: 'Il n\'y a pas de son', value: 6 }
];

const RATE_VIDEO_MAP = [
  { label: 'Excellente', value: 7 },
  { label: 'Bonne', value: 8 },
  { label: 'Standard', value: 9 },
  { label: 'Insuffisante', value: 10 }
];

const toggleAutoPlay = (player, comingNext) => {
  player.store.dispatch({
    type: UPDATE_SETTING,
    payload: {
      comingNext: !comingNext.value
    }
  });

  player.trigger(
    USER_SETTINGS_UPDATED,
    { autoplay: !comingNext.value }
  );
};

function Settings(props) {
  const {
    isAd,
    isDVR,
    isLive,
    waitStart,
    comingNext,
    small,
    extraSmall,
    mobile,
    hasQualities,
    rate,
    reporter,
    currentQualityLabel,
    mappedQualities,
    isFullscreen,
    audioSelected,
    audiosAvailable,
    subtitleSelected,
    subtitlesAvailable,
    textTrack,
    audioTrack,
    subtitlesSettings,
    containerHeight,
    containerWidth,
    isEmbed
  } = props;

  const player = usePlayerContext();
  const setStyles = useStyles();

  const [opened, setOpened] = useState(false);
  const [previousActiveIndex, setPreviousActiveIndex] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef(null);
  const closeIconRef = useRef(null);
  const iconRef = useRef(null);
  const [openCloseIconState, setOpenCloseIconState] = useState({ label: 'Paramètres', describedBy: undefined });

  useRegisterDialog({ id: 'settings', openingMode: 'click', state: [opened, setOpened] });

  // Store navItemsRefs to handle "Back" navigation focus
  const navItemsRefs = useRef({});
  const setNavItemRef = (el, index) => {
    if (el) {
      navItemsRefs.current[index] = el;
      return;
    }
    delete navItemsRefs.current[index];
  };

  const [total, settingsList] = useMemo(
    () => {
      const defaultSetting = { index: -1, isEnabled: false, count: 1 };
      const settings = {
        autoPlay: {
          ...defaultSetting,
          isEnabled: comingNext.enabled,
          navigation: 0
        },
        quality: {
          ...defaultSetting,
          isEnabled: hasQualities,
          navigation: 1
        },
        speed: {
          ...defaultSetting,
          isEnabled: !isDVR && !isLive,
          navigation: 2
        },
        audio: {
          ...defaultSetting,
          isEnabled: audiosAvailable.length > 1,
          navigation: 3
        },
        subtitles: {
          ...defaultSetting,
          isEnabled: subtitlesAvailable.length > 1,
          navigation: 4
        },
        subtitlesSettings: {
          ...defaultSetting,
          isEnabled: subtitlesAvailable.length > 1,
          navigation: 5
        },
        reports: {
          ...defaultSetting,
          isEnabled: reporter,
          count: 2,
          navigation: 6
        },
        embed: {
          ...defaultSetting,
          isEnabled: isEmbed,
          navigation: 7
        },
        feedback: {
          isEnabled: true, // not need index (...settings) because feedback is not listed a list
          navigation: 8
        }
      };

      const { totalList, settingsWithIndex } = Object.keys(settings).reduce((acc, key) => {
        if (settings[key].isEnabled) {
          settings[key].index = acc.totalList + 1;
          acc.totalList += settings[key].count;
        }
        acc.settingsWithIndex[key] = settings[key];
        return acc;
      }, { totalList: 0, settingsWithIndex: {} });

      return [totalList, settingsWithIndex];
    },
    [
      comingNext.enabled,
      hasQualities,
      isDVR,
      isLive,
      audiosAvailable.length,
      subtitlesAvailable.length,
      reporter,
      isEmbed
    ]
  );

  // Handle focus on main slide (activeIndex = 0) and `feedback` slide
  useEffect(() => {
    requestAnimationFrame(() => {
      if (activeIndex === 0 && previousActiveIndex > 0 && navItemsRefs?.current[previousActiveIndex]) {
        navItemsRefs.current[previousActiveIndex].focus();
        return;
      }

      if ([0, settingsList.feedback.navigation].includes(activeIndex)) {
        closeIconRef.current?.focus();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Do not add previousActiveIndex in deps here
  }, [activeIndex, settingsList.feedback.navigation]);

  useEffect(() => {
    if (activeIndex === settingsList.feedback.navigation) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setOpened(false);
        setActiveIndex(0);
      }, 10000);
    } else {
      clearInterval(timerRef.current);
    }
  }, [activeIndex, settingsList.feedback.navigation]);

  useEffect(() => {
    if (!opened) {
      setOpenCloseIconState({ label: 'Paramètres' });
      /* We reset active slide on close to 0 here.
         Since SlideMenu is unmounted on close, his own useEffect will never fire. */
      setActiveIndex(0);
      return;
    }

    if (activeIndex === settingsList.reports.navigation) setOpenCloseIconState({ label: 'Merci pour votre retour, fermer la fenêtre de signalement', describedBy: 'feedback-thanks-text' });
    else setOpenCloseIconState({ label: 'Fermer la fenêtre de Paramètres et retourner au lecteur vidéo' });
  }, [opened, activeIndex, settingsList.reports.navigation]);

  useOpenDialogsCounter(opened);

  /* Avoid error when manipulating playback rate manually */
  const selectedRate = PLAYBACK_SPEED_MAP.find((speed) => speed.value === Math.max(0.25, Math.min(rate, 1.75)));

  const rowItemStyle = setStyles(TEXT_LEVEL_2_FONT_SIZE);

  const closeModal = (event) => handleCloseModalButton({
    event,
    player,
    currentIconRef: iconRef.current,
    setIsModalOpened: setOpened,
    closedAction: SETTINGS_CLOSED
  });

  const styleModal = useMemo(
    () => deepMerge(
      {
        default: activeIndex === 0 || activeIndex !== settingsList.subtitlesSettings.navigation
          ? { height: 'auto' }
          : {},
        'small+extraSmall': { height: '100%' }
      },
      ((activeIndex === settingsList.subtitlesSettings.navigation) && {
        'large+extraLarge': {
          maxHeight: containerHeight > 820 ? '100%' : containerHeight * 0.8, // 820 threshold for scroll
          height: containerHeight > 820 ? 'auto' : containerHeight * 0.8
        },
        medium: {
          maxHeight: containerHeight > containerWidth ? '100%' : containerHeight * 0.85,
          height: containerHeight > containerWidth ? 'auto' : containerHeight * 0.85
        }
      })
    ),
    [activeIndex, settingsList.subtitlesSettings.navigation, containerHeight, containerWidth]
  );

  return (
    <Fragment>
      <Button
        type="icon"
        openingMode="click"
        label="Paramètres"
        forceLabel
        zone="bottom"
        last
        onClick={() => {
          setOpened(true);

          player.userEvents$.next({
            action: SETTINGS_OPENED,
            source: USER_CLICK
          });
        }}
        xs={(small || extraSmall)}
        iconRef={iconRef}
        name="btn-settings"
        ariaHidden={false}
        ariaLabel="Paramètres"
        hidden={isAd || waitStart || ((isLive || isDVR) && (!comingNext.enabled && !hasQualities && !reporter))}
        icon="settings"
        size={
          (setStyles(BUTTON_SQUARE_SIZE)).width
        }
      />

      {opened && (
        <Modal
          trap
          show={opened}
          attachedTo="btn-settings"
          onChange={(openedState) => {
            setOpened(openedState);
          }}
          onEscapeKeyDown={closeModal}
          closingButtonStyle={
            setStyles({
              default: { display: 'flex', position: 'absolute', top: 25, right: 25, zIndex: BUTTON_LAYER },
              extraSmall: { right: 24 }
            })
          }
          styles={styleModal}
          closeComponent={(
            <Button
              type="icon"
              openingMode="click"
              label="Fermer"
              ariaLabel={openCloseIconState.label}
              forceLabel
              disableFocus={false}
              last
              zone="bottom"
              onClick={closeModal}
              icon="close"
              xs
              iconRef={closeIconRef}
              name="btn-close-settings"
              ariaHidden={false}
              size={setStyles(SETTINGS_ICON_SIZE).size}
            />
          )}
        >
          <SlideMenu
            styles={{
              navbar: setStyles(NAVBAR_STYLE),
              navbarTitle: setStyles({
                default: { height: 40 },
                medium: { height: 30 },
                small: { height: 33 },
                extraSmall: { height: 24 }
              })
            }}
            opened={opened}
            onNavigate={(index) => {
              setPreviousActiveIndex(activeIndex);
              setActiveIndex(index);
            }}
          >
            {({ navigateTo }) => (!opened ? <div /> : (
              <Fragment>

                {/* MAIN SETTINGS OPTIONS SLIDE */}
                <SlidableSlide title="Paramètres" isFullscreen={isFullscreen}>
                  <div>
                    <SlideContent>
                      {settingsList.autoPlay.isEnabled && activeIndex === 0 && (
                        <RowItem
                          label={`Lecture automatique (${comingNext.value ? 'activé' : 'désactivé'})`}
                          ariaLabel="Lecture automatique"
                          index={settingsList.autoPlay.index}
                          totalCount={total}
                          ariaValue={() => (comingNext.value ? 'activé' : 'désactivé')}
                          onClick={() => toggleAutoPlay(player, comingNext)}
                          style={rowItemStyle}
                        >
                          <ToggleButton
                            val={comingNext.value}
                            onToggle={() => toggleAutoPlay(player, comingNext)}
                            ariaLabel={`${comingNext.value ? 'Désactiver' : 'Activer'} la lecture automatique`}
                          />
                        </RowItem>
                      )}
                      {settingsList.quality.isEnabled && activeIndex === 0 && (
                        <RowItem
                          label="Qualité vidéo"
                          index={settingsList.quality.index}
                          ref={(el) => setNavItemRef(el, settingsList.quality.navigation)}
                          totalCount={total}
                          sublabel={currentQualityLabel}
                          sublabelIcon={mappedQualities.find((q) => q.label === currentQualityLabel)?.isEco && 'eco-activated'}
                          sublabelIconStyle={ROWITEM_SUBLABEL_ICON}
                          onClick={() => {
                            navigateTo(settingsList.quality.navigation);
                            player.userEvents$.next({
                              action: USER_QUALITY_MENU_OPENED,
                              source: USER_CLICK,
                              value: currentQualityLabel
                            });
                          }}
                          link
                          style={rowItemStyle}
                        />
                      )}
                      {settingsList.speed.isEnabled && activeIndex === 0 && (
                        <RowItem
                          label={`Vitesse de lecture ${!mobile ? '(x)' : ''}`}
                          ariaLabel="Vitesse de lecture raccourci touche x"
                          index={settingsList.speed.index}
                          ref={(el) => setNavItemRef(el, settingsList.speed.navigation)}
                          totalCount={total}
                          sublabel={selectedRate.label}
                          onClick={() => {
                            navigateTo(settingsList.speed.navigation);
                            player.userEvents$.next({
                              action: USER_PLAYBACKRATE_CHANGE,
                              value: { prevRate: rate }
                            });
                          }}
                          link
                          style={rowItemStyle}
                        />
                      )}
                      {settingsList.audio.isEnabled && activeIndex === 0 && (
                        <RowItem
                          label={`Audio ${!mobile ? '(b)' : ''}`}
                          ariaLabel="Audio"
                          index={settingsList.audio.index}
                          ref={(el) => setNavItemRef(el, settingsList.audio.navigation)}
                          totalCount={total}
                          sublabel={audioTrack?.label}
                          sublabelIcon={audioTrack?.icon}
                          sublabelIconStyle={ROWITEM_SUBLABEL_ICON}
                          onClick={() => {
                            navigateTo(settingsList.audio.navigation);
                            player.userEvents$.next({
                              action: USER_AUDIO_TRACK_LIST_OPENED,
                              source: USER_CLICK,
                              value: audioTrack
                            });
                          }}
                          link
                          style={rowItemStyle}
                        />
                      )}
                      {settingsList.subtitles.isEnabled && activeIndex === 0 && (
                        <RowItem
                          label={`Sous-titres ${!mobile ? '(c)' : ''}`}
                          ariaLabel="Sous-titres"
                          index={settingsList.subtitles.index}
                          ref={(el) => setNavItemRef(el, settingsList.subtitles.navigation)}
                          totalCount={total}
                          sublabelIcon={textTrack?.icon}
                          sublabelIconStyle={ROWITEM_SUBLABEL_ICON}
                          sublabel={textTrack?.label || 'Aucun'}
                          onClick={() => {
                            navigateTo(settingsList.subtitles.navigation);
                            player.userEvents$.next({
                              action: USER_SUBTITLE_TRACK_LIST_OPENED,
                              source: USER_CLICK,
                              value: textTrack
                            });
                          }}
                          link
                          style={rowItemStyle}
                        />
                      )}
                      {settingsList.subtitlesSettings.isEnabled && activeIndex === 0 && (
                        <RowItem
                          label="Paramètres des sous-titres"
                          ariaLabel="Paramètres des sous-titres"
                          ref={(el) => setNavItemRef(el, settingsList.subtitlesSettings.navigation)}
                          onClick={() => {
                            navigateTo(settingsList.subtitlesSettings.navigation);
                            player.userEvents$.next({
                              action: USER_SUBTITLE_SETTINGS_OPENED,
                              source: USER_CLICK,
                              value: subtitlesSettings
                            });
                          }}
                          link
                          style={rowItemStyle}
                        />
                      )}
                    </SlideContent>
                    {settingsList.reports.isEnabled && activeIndex === 0 && (
                      <Fragment>
                        <Seperator />
                        <SlideContent>
                          <RowItem
                            index={settingsList.reports.index}
                            ref={(el) => setNavItemRef(el, settingsList.reports.navigation)}
                            totalCount={total}
                            label="Signaler un problème"
                            onClick={() => navigateTo(settingsList.reports.navigation)}
                            link
                            style={rowItemStyle}
                            ignorePosition
                          />
                          <RowItem
                            index={settingsList.reports.index + 1}
                            ref={(el) => setNavItemRef(el, settingsList.reports.navigation + 1)}
                            totalCount={total}
                            label="Noter la qualité de la vidéo"
                            onClick={() => navigateTo(settingsList.reports.navigation + 1)}
                            link
                            ignorePosition
                            style={rowItemStyle}
                          />
                        </SlideContent>
                      </Fragment>
                    )}
                    {settingsList.embed.isEnabled && activeIndex === 0 && (
                      <Fragment>
                        <Seperator />
                        <SlideContent>
                          <RowItem
                            label="Politique de gestion des traceurs France Télevisions"
                            index={settingsList.embed.index}
                            totalCount={total}
                            onClick={() => {
                              window.open(
                                'https://www.francetelevisions.fr/groupe/confidentialite/gestion-des-cookies',
                                '_blank'
                              );
                            }}
                            link
                            linkIcon="link"
                            linkIconStyle={{}}
                            ignorePosition
                            style={rowItemStyle}
                          />
                        </SlideContent>
                      </Fragment>
                    )}
                  </div>
                </SlidableSlide>

                {/* VIDEO QUALITY */}
                {hasQualities && activeIndex === settingsList.quality.navigation && (
                  <SlidableSlide title="Qualité vidéo" isFullscreen={isFullscreen}>
                    <SlideContent>
                      <Quality />
                    </SlideContent>
                  </SlidableSlide>
                )}
                {/* PLAYBACK RATE */}
                {activeIndex === settingsList.speed.navigation && (
                  <SlidableSlide title={`Vitesse de lecture ${!mobile ? '(x)' : ''}`} isFullscreen={isFullscreen}>
                    <SlideContent>
                      {PLAYBACK_SPEED_MAP.map(({ label, ariaLabel, value }, i) => (
                        <Selectable
                          radioButton
                          key={label}
                          label={label}
                          ariaLabel={ariaLabel}
                          index={i + 1}
                          value={value}
                          totalCount={PLAYBACK_SPEED_MAP.length}
                          active={rate === value}
                          onSelect={() => {
                            player.speed(value); player.userEvents$.next({
                              action: USER_PLAYBACKRATE_CHANGE,
                              source: USER_CLICK,
                              value: { prevRate: rate, newRate: value }
                            });
                          }}
                        />
                      ))}

                    </SlideContent>
                  </SlidableSlide>
                )}

                {/* Audio */}
                {activeIndex === settingsList.audio.navigation && (
                  <SlidableSlide title={`Audio ${!mobile ? '(b)' : ''}`} isFullscreen={isFullscreen}>
                    <SlideContent>
                      {audiosAvailable.map(({ label, icon, ariaLabel, language, index }, i) => (
                        <Selectable
                          radioButton
                          key={label}
                          label={label}
                          ariaLabel={ariaLabel}
                          index={i + 1}
                          icon={icon}
                          value={language}
                          totalCount={audiosAvailable.length}
                          active={audioSelected === index}
                          onSelect={() => {
                            player.userEvents$.next({
                              action: USER_AUDIO_TRACK_CHANGED,
                              source: USER_CLICK,
                              value: { label, language, icon }
                            });
                            player.setAudioTrack(index);
                          }}
                        />
                      ))}
                    </SlideContent>
                  </SlidableSlide>
                )}

                {/* Subtitles */}
                {activeIndex === settingsList.subtitles.navigation && (
                  <SlidableSlide title={`Sous-titres ${!mobile ? '(c)' : ''}`} isFullscreen={isFullscreen}>
                    <SlideContent>
                      {subtitlesAvailable.map(({ label, icon, ariaLabel, language, index }, i) => (
                        <Selectable
                          radioButton
                          key={label}
                          label={label}
                          ariaLabel={ariaLabel}
                          index={i + 1}
                          icon={icon}
                          value={language}
                          totalCount={subtitlesAvailable.length}
                          active={subtitleSelected === index}
                          onSelect={() => {
                            player.userEvents$.next({
                              action: USER_TEXT_TRACK_CHANGED,
                              source: USER_CLICK,
                              value: { label, language, icon }
                            });
                            player.setSubtitleTrack(index);
                          }}
                        />
                      ))}
                    </SlideContent>
                  </SlidableSlide>
                )}
                {/* Subtitles Settings */}
                {activeIndex === settingsList.subtitlesSettings.navigation && (
                  <SlidableSlide
                    title="Paramètres des sous-titres"
                    isFullscreen={isFullscreen}
                    scrollable={false}
                  >
                    <SlideContent>
                      <TextTrackSettings onSave={closeModal} />
                    </SlideContent>
                  </SlidableSlide>
                )}

                {/* REPORT PROBLEM */}
                {activeIndex === settingsList.reports.navigation && (
                  <Reporter
                    closeIconRef={closeIconRef}
                    title="Signaler un problème"
                    source={REPORT_PROBLEM_MAP}
                    onClick={() => {
                      player.userEvents$.next({ action: USER_REPORT_PROBLEM });
                    }}
                    submit={{
                      label: 'Signaler',
                      onSubmit: (value) => {
                        player.report(1, value); navigateTo(settingsList.feedback.navigation);
                      }
                    }}
                    isFullscreen={isFullscreen}
                  />
                )}

                {/* REPORT VIDEO QUALITY */}
                {activeIndex === settingsList.reports.navigation + 1 && (
                  <Reporter
                    closeIconRef={closeIconRef}
                    title="Noter la qualité vidéo"
                    source={RATE_VIDEO_MAP}
                    onClick={() => {
                      player.userEvents$.next({ action: USER_REPORT_QUALITY });
                    }}
                    submit={{
                      label: 'Envoyer',
                      onSubmit: (value) => {
                        player.report(2, value); navigateTo(settingsList.feedback.navigation);
                      }
                    }}
                    isFullscreen={isFullscreen}
                  />
                )}

                {activeIndex === settingsList.feedback.navigation && (
                  <div style={{ ...FEEDBACK_THANKS_TEXT_STYLE, ...setStyles(TEXT_LEVEL_2_FONT_SIZE) }}>
                    <span role="heading" aria-level="3" id="feedback-thanks-text" aria-label="Merci pour votre retour">Merci pour votre retour</span>
                  </div>
                )}

              </Fragment>
            ))}
          </SlideMenu>
        </Modal>
      )}

    </Fragment>
  );
}

const selector = ({
  playback: { hasQualities, rate, currentQualityLabel, mappedQualities },
  ui: { waitStart, reporter, isFullscreen },
  settings: { comingNext },
  subtitles: { settings },
  media: {
    isAd,
    isDVR,
    isLive,
    subtitlesAvailable,
    subtitleSelected,
    textTrack,
    audioTrack,
    audiosAvailable,
    audioSelected
  },
  ui: { originUrl }
}) => ({
  waitStart,
  comingNext,
  isAd,
  hasQualities: hasQualities && Boolean(mappedQualities.length),
  rate,
  isDVR,
  isLive,
  reporter,
  isEmbed: Boolean(originUrl),
  currentQualityLabel,
  mappedQualities,
  isFullscreen,
  subtitlesAvailable,
  subtitleSelected,
  textTrack,
  audioTrack,
  audiosAvailable,
  audioSelected,
  subtitlesSettings: settings
});

export default connect(selector)(withBreakPoints(Settings));
